export interface Mapper {
  /** @format GUID */
  id?: string | null;
  /**
   * The FQDN of the entity the mapper refer to of exist
   * @maxLength 1000
   */
  entityFqdn?: string;
  /**
   * The entity fqn - this is unique across wix
   * @maxLength 1000
   */
  entityFqn?: string;
  /**
   * The appDefId that the entity belongs to
   * @format GUID
   */
  appId?: string;
  /**
   * A list of fields to translate from the entity
   * @maxSize 1000
   */
  fields?: MapperField[];
  /**
   * The name of field that hold the parent name (if exist)
   * @maxLength 1000
   */
  groupByNameFieldId?: string | null;
  /** Flag to turn off sync data to localization upon domain events */
  ignoreDomainEvents?: boolean | null;
  /**
   * The segment of the entity
   * @maxLength 20
   */
  segment?: string | null;
  /** flag that indicate the removing a repeated field in main language should remove it in all languages */
  deleteRepeatedFields?: boolean | null;
  /** Whether the entity can bt extended by Data Extensions */
  extensible?: boolean | null;
  /**
   * The schema id that the mapper points to
   * @format GUID
   */
  schemaId?: string | null;
}

export interface MapperField {
  /**
   * The field FQN
   * @maxLength 1000
   */
  id?: string;
  /** The field Type as it's shown in localization schema */
  type?: FieldTypeWithLiterals;
  /**
   * If the field is part of a repeated fields
   * @maxSize 16
   */
  sequencePath?: SequencePath[];
  /**
   * Option to override the field mask (the default is the id)
   * @maxLength 1000
   */
  overrideMask?: string | null;
}

export enum FieldType {
  /** Undefined field type */
  UNDEFINED_TYPE = 'UNDEFINED_TYPE',
  /** Short text TEXT */
  SHORT_TEXT = 'SHORT_TEXT',
  /** Long Plain Text TEXT */
  LONG_TEXT = 'LONG_TEXT',
  /** Long text including styles, images, links and more... HTML */
  RICH_TEXT = 'RICH_TEXT',
  /** Wix Rich-Content-Editor format RICO */
  RICH_CONTENT_EDITOR = 'RICH_CONTENT_EDITOR',
  /** Choose one from many options CSV TEXT */
  SELECTION = 'SELECTION',
  /** Choose multi items from many options */
  MULTI_SELECTION = 'MULTI_SELECTION',
  /** Wix document media item */
  DOCUMENT = 'DOCUMENT',
  /** Wix Image media item */
  IMAGE = 'IMAGE',
  /** Wix Video media item */
  VIDEO = 'VIDEO',
  /** Image URL without metadata */
  IMAGE_LINK = 'IMAGE_LINK',
}

/** @enumType */
export type FieldTypeWithLiterals =
  | FieldType
  | 'UNDEFINED_TYPE'
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'RICH_TEXT'
  | 'RICH_CONTENT_EDITOR'
  | 'SELECTION'
  | 'MULTI_SELECTION'
  | 'DOCUMENT'
  | 'IMAGE'
  | 'VIDEO'
  | 'IMAGE_LINK';

export interface SequencePath {
  /**
   * The name of the sequence the field is a part of
   * @maxLength 1000
   */
  name?: string;
  /**
   * The name of the field hold the id in the entity
   * @maxLength 1000
   */
  fieldId?: string;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface CreateOrUpdateNileMapperRequest {
  /** A mapper to create or update if already exist */
  mapper?: Mapper;
  /** if true don't send pods notify kafka event */
  skipPodsNotify?: boolean | null;
}

export interface CreateOrUpdateNileMapperResponse {}

export interface GetEntityMapperRequest {
  /**
   * App id of the mapper requested
   * @format GUID
   */
  appId?: string;
  /**
   * Entity fqn of the mapper requested
   * @maxLength 100
   */
  entityFqn?: string;
}

export interface GetEntityMapperResponse {
  /** Mapper requested */
  entityMapper?: Mapper;
}

export interface ListEntityMapperRequest {
  /**
   * App id of the mapper requested
   * @format GUID
   */
  appId?: string | null;
  /**
   * Entity fqn of the mapper requested
   * @maxLength 100
   */
  entityFqn?: string | null;
}

export interface ListEntityMapperResponse {
  /**
   * Mapper requested
   * @maxSize 500
   */
  mappers?: Mapper[];
}

export interface DeleteEntityMapperRequest {
  /**
   * App id of the mapper to delete
   * @format GUID
   */
  appId?: string;
  /**
   * Entity fqn of the mapper to delete
   * @maxLength 100
   */
  entityFqn?: string;
}

export interface DeleteEntityMapperResponse {}

export interface GetSubscribedTopicsRequest {}

export interface GetSubscribedTopicsResponse {
  /**
   * List of subscribed topics
   * @maxSize 1000
   */
  topics?: string[];
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface HtmlNewRevisionSavedMessage {
  /**
   * HTML Site ID
   * @format GUID
   */
  siteId?: string;
  /** Newly saved revision */
  revision?: string;
  /** Restored from revision */
  restoredFrom?: string | null;
  /**
   * Optional meta site id
   * @format GUID
   */
  metaSiteId?: string | null;
  /** Date that this version was updated (same as created) */
  updateDate?: string;
  /** determine whether this site uses responsive editor */
  isResponsive?: boolean;
  /**
   * optional branch id if revision saved on branch
   * @format GUID
   */
  branchId?: string | null;
  /** determine if the new revision is a result of override save */
  overrideSave?: boolean;
  /**
   * Optional last transaction id
   * @maxLength 50
   */
  lastTransactionId?: string | null;
  /**
   * optional branch id if revision restored from branch
   * @format GUID
   */
  restoredFromBranchId?: string | null;
  /** information of revision prior to save revision operation */
  lastRevisionInfo?: RevisionInfo;
  /** new revision pages */
  pages?: Page[];
  /** the pages that were changed from the last save. Operations such as clone and restore will return an empty response at the moment. */
  changedPages?: ChangedPages;
  /**
   * id of the main page of the site
   * @maxLength 100
   */
  mainPageId?: string;
  /**
   * correlation id of original process (allows callers of createRevision to identify their revisions)
   * @format GUID
   */
  originCorrelationId?: string | null;
}

export interface RevisionInfo {
  /** revision number */
  revision?: string;
  /** last_transaction_id of revision */
  lastTransactionId?: string | null;
}

export interface Page {
  /**
   * Page's Id
   * @maxLength 100
   */
  id?: string;
}

export interface ChangedPages {
  /** list of updated_pages */
  updatedPages?: Page[];
  /** list of deleted_pages */
  deletedPages?: Page[];
}

export interface SyncEditorDataRequest {
  /** an optional filter for syncing the content */
  syncFilter?: SyncFilter;
}

export interface SyncFilter {
  /**
   * content IETF BCP 47 language tag
   * @format LANGUAGE_TAG
   */
  languageTag?: string;
}

export interface SyncEditorDataResponse {}

export interface PublishSiteTranslationsRequest {
  /**
   * A language to publish it's language
   * @format LANGUAGE
   */
  language?: string;
}

export interface PublishSiteTranslationsResponse {}

export interface CreateOrUpdateEditorMapperRequest {
  /** A mapper to create or update if already exist */
  mapper?: Mapper;
}

export interface CreateOrUpdateEditorMapperResponse {}

export interface GetMapperRequest {
  /**
   * The mapper type requested
   * @minLength 2
   * @maxLength 80
   */
  entityType?: string;
}

export interface GetMapperResponse {
  /** The mapper with the entity type requested */
  mapper?: Mapper;
}

export interface DeleteMapperRequest {
  /**
   * The entity type to delete from the service
   * @minLength 2
   * @maxLength 80
   */
  entityType?: string;
}

export interface DeleteMapperResponse {}

export interface ListMappersRequest {}

export interface ListMappersResponse {
  /**
   * all mappers known to editor adapter
   * @maxSize 256
   */
  mappers?: Mapper[];
}
